import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { TopBar } from '@/components/TopBar';
import { AppCard } from '@/components/AppCard';
import { HeroLogo } from '@/components/HeroLogo';
import { HealthDot } from '@/components/HealthDot';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, PackageOpen, RefreshCw, Activity, UserCheck } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const canManage = user?.permission === 'manage';
  const domain = settings?.domain || 'cxatools.com';

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['apps', 'enabled'],
    queryFn: async () => (await api.get('/apps', { params: { enabled_only: true } })).data,
  });

  const { data: health } = useQuery({
    queryKey: ['apps', 'health'],
    queryFn: async () => (await api.get('/apps/health')).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const { data: team = [] } = useQuery({
    queryKey: ['team'],
    queryFn: async () => (await api.get('/team')).data,
    enabled: canManage,
    refetchInterval: 60000,
  });
  const pendingCount = useMemo(() => team.filter((u) => u.status === 'pending').length, [team]);

  const healthMap = useMemo(() => {
    const m = {};
    (health?.results || []).forEach((r) => { m[r.id] = r; });
    return m;
  }, [health]);

  const refreshHealth = async () => {
    setRefreshing(true);
    try {
      const data = (await api.get('/apps/health', { params: { force: true } })).data;
      qc.setQueryData(['apps', 'health'], data);
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return apps;
    return apps.filter((a) => [a.name, a.description, a.category, a.slug].filter(Boolean).some((v) => v.toLowerCase().includes(term)));
  }, [apps, q]);

  const stats = useMemo(() => {
    let online = 0, issues = 0, checking = 0;
    apps.forEach((a) => {
      const st = healthMap[a.id]?.status;
      if (st === 'online') online++;
      else if (st === 'offline' || st === 'degraded') issues++;
      else checking++;
    });
    return { online, issues, checking, total: apps.length };
  }, [apps, healthMap]);

  const lastChecked = health?.checked_at ? new Date(health.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null;

  return (
    <div className="cx-page pb-16">
      <TopBar />
      <main className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8">
        <section className="pt-9 pb-5">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="font-mono text-xs text-[color:var(--cx-primary)] tracking-wide">OPERATIONS CONSOLE · {domain}</p>
              <div className="mt-3">
                <HeroLogo settings={settings} />
              </div>
              <p className="mt-3 text-[color:var(--cx-fg-muted)] max-w-xl">
                {canManage ? 'Launch and monitor your tools.' : 'Launch your team tools.'} Reach any directly at
                <span className="font-mono text-[color:var(--cx-fg)]"> /{'{shortcut}'}</span>.
              </p>
            </div>

            <div className="cx-glass rounded-2xl px-4 py-3 flex items-center gap-4 shrink-0" data-testid="dashboard-status-strip">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5 text-[color:var(--cx-muted)]" />
                  <div className="leading-none">
                    <div className="font-display text-lg text-[color:var(--cx-fg)]">{stats.total}</div>
                    <div className="text-[10px] text-[color:var(--cx-muted)] uppercase tracking-wide">apps</div>
                  </div>
                </div>
                <div className="h-8 w-px bg-[color:var(--cx-border)]" />
                <div className="flex items-center gap-2" data-testid="stat-online">
                  <HealthDot status="online" />
                  <div className="leading-none">
                    <div className="font-display text-lg text-[color:var(--cx-fg)]">{stats.online}</div>
                    <div className="text-[10px] text-[color:var(--cx-muted)] uppercase tracking-wide">online</div>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-testid="stat-issues">
                  <HealthDot status={stats.issues > 0 ? 'offline' : 'unconfigured'} />
                  <div className="leading-none">
                    <div className="font-display text-lg text-[color:var(--cx-fg)]">{stats.issues}</div>
                    <div className="text-[10px] text-[color:var(--cx-muted)] uppercase tracking-wide">issues</div>
                  </div>
                </div>
              </div>
              <button onClick={refreshHealth} disabled={refreshing} title={lastChecked ? `Updated ${lastChecked}` : 'Refresh status'} data-testid="dashboard-refresh-health"
                className="h-8 w-8 rounded-lg flex items-center justify-center text-[color:var(--cx-muted)] hover:text-[color:var(--cx-primary)] hover:bg-[color:var(--cx-surface-solid)] transition-colors">
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </motion.div>

          {/* Pending approvals banner (managers) */}
          {canManage && pendingCount > 0 && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="mt-5 rounded-2xl border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-4 py-3 flex items-center gap-3" data-testid="dashboard-pending-banner">
              <span className="h-9 w-9 rounded-xl bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center">
                <UserCheck className="h-4 w-4 text-[color:var(--cx-primary)]" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[color:var(--cx-fg)]">{pendingCount} access {pendingCount === 1 ? 'request' : 'requests'} awaiting approval</div>
                <div className="text-xs text-[color:var(--cx-fg-muted)]">Review and approve new team members.</div>
              </div>
              <Button asChild size="sm"><Link to="/settings?tab=team" data-testid="dashboard-review-requests">Review requests</Link></Button>
            </motion.div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search apps…" className="pl-9" data-testid="dashboard-search-input" />
            </div>
          </div>
        </section>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {Array.from({ length: 6 }).map((_, i) => (<Skeleton key={i} className="h-44 rounded-2xl bg-[color:var(--cx-surface-solid)]" />))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="cx-glass rounded-2xl p-12 text-center" data-testid="dashboard-empty">
            <div className="mx-auto h-12 w-12 rounded-xl bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center">
              {apps.length === 0 ? <PackageOpen className="h-6 w-6 text-[color:var(--cx-muted)]" /> : <Search className="h-6 w-6 text-[color:var(--cx-muted)]" />}
            </div>
            <h3 className="mt-4 font-display text-lg text-[color:var(--cx-fg)]">{apps.length === 0 ? 'No apps yet' : 'No matches'}</h3>
            <p className="mt-1 text-sm text-[color:var(--cx-fg-muted)]">{apps.length === 0 ? (canManage ? 'Add your first app to see it here.' : 'Ask an admin to add apps.') : 'Try a different search term.'}</p>
            {apps.length === 0 && canManage && (<Button asChild className="mt-5"><Link to="/admin"><Plus className="h-4 w-4 mr-1.5" />Add an app</Link></Button>)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((app, i) => (<AppCard key={app.id} app={app} index={i} health={healthMap[app.id]} />))}
          </div>
        )}
      </main>
    </div>
  );
}
